import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Download, FileText, Loader2, Scale, Search, Undo2 } from 'lucide-react';
import { GlobalFilters } from '@/components/po-grn/GlobalFilters';
import { KpiGrid } from '@/components/po-grn/KpiGrid';
import { OverviewCharts } from '@/components/po-grn/OverviewCharts';
import { AnalyticsTable } from '@/components/po-grn/AnalyticsTable';
import ReportDownloadLog from '@/components/ReportDownloadLog';
import { endOfMonth, format, parseISO } from 'date-fns';
import { PeriodComparison } from '@/components/po-grn/PeriodComparison';
import { MrpMismatchBanner, MrpMismatchPanel } from '@/components/po-grn/MrpMismatchAlerts';
import { MarginBreakdown } from '@/components/po-grn/MarginBreakdown';
import { comparisonRanges } from '@/components/po-grn/periods';
import { PRESETS } from '@/components/po-grn/DateRangeFilter';
import { RECON_COLS, PPV_COLS, MRP_COLS, SUPPLIER_COLS, EXCEPTION_COLS } from '@/components/po-grn/columns';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

const toParams = (filters, search) => {
  const p = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (k.endsWith('_preset')) return;
    if (Array.isArray(v) ? v.length : v) p[k] = Array.isArray(v) ? v.join('|') : v;
  });
  if (search.trim()) p.search = search.trim();
  return p;
};

const TABS = [
  ['overview', '1. Overview'], ['reconciliation', '2. Reconciliation'], ['price', '3. Price Variance'],
  ['mrp', '4. MRP & Margin'], ['supplier', '5. Supplier Performance'], ['exceptions', '6. Exceptions'],
];

export default function POGRNAnalytics() {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [tab, setTab] = useState('overview');
  const [history, setHistory] = useState([]);
  const snap = useRef({});
  snap.current = { filters, search, tab };

  const scroller = () => {
    const m = document.querySelector('main');
    return m && m.scrollHeight > m.clientHeight ? m : document.scrollingElement;
  };

  const pushView = useCallback((label) => {
    setHistory((h) => [...h.slice(-19), { ...snap.current, scroll: scroller()?.scrollTop || 0, label }]);
  }, []);

  const goBack = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    setHistory(history.slice(0, -1));
    setFilters(prev.filters);
    setSearch(prev.search);
    setTab(prev.tab);
    setTimeout(() => { const el = scroller(); if (el) el.scrollTo({ top: prev.scroll, behavior: 'smooth' }); }, 80);
  };

  const changeTab = (t) => { if (t !== tab) { pushView(`Opened ${TABS.find(([k]) => k === t)?.[1].replace(/^\d+\.\s*/, '')} tab`); setTab(t); } };
  const [logRefresh, setLogRefresh] = useState(0);

  useEffect(() => {
    axios.get(`${API}/reports/po-grn/filter-options`, { headers: authHeaders() })
      .then((r) => setOptions(r.data)).catch(() => toast.error('Failed to load filter options'));
  }, []);

  const cmp = useMemo(() => comparisonRanges(filters.po_date_from, filters.po_date_to, filters.po_date_preset,
    PRESETS.find(([k]) => k === filters.po_date_preset)?.[1]), [filters.po_date_from, filters.po_date_to, filters.po_date_preset]);

  const params = useMemo(() => {
    const p = toParams(filters, search);
    if (cmp) Object.assign(p, { cmp_cur_from: cmp.cur[0], cmp_cur_to: cmp.cur[1], cmp_prev_from: cmp.prev[0], cmp_prev_to: cmp.prev[1] });
    return p;
  }, [filters, search, cmp]);

  const drill = useCallback((kind, value) => {
    if (!value || String(value).startsWith('Others') || value === 'Uncategorised') return;
    const patch = {
      month: () => {
        const start = parseISO(`${value}-01`);
        return { po_date_from: format(start, 'yyyy-MM-dd'), po_date_to: format(endOfMonth(start), 'yyyy-MM-dd'), po_date_preset: 'custom' };
      },
      supplier: () => ({ suppliers: [value] }),
      recon: () => ({ recon_statuses: [value] }),
      category: () => ({ categories: [value] }),
      brand: () => ({ brands: [value] }),
    }[kind]();
    pushView(`Drilled into ${kind}: ${value}`);
    setFilters((f) => ({ ...f, ...patch }));
    toast.info(`Drilled into ${kind}: ${value}`);
  }, [pushView]);

  const pickMismatch = useCallback((r) => {
    pushView(`Opened MRP alert ${r.sku} / ${r.grn_number}`);
    setFilters((f) => ({ ...f, skus: [r.sku], grn_numbers: [r.grn_number] }));
    toast.info(`Filtered to ${r.sku} on ${r.grn_number}`);
  }, [pushView]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/reports/po-grn/analytics`, { headers: authHeaders(), params, signal: ctrl.signal });
        setData(r.data);
        setLoading(false);
      } catch (e) {
        if (axios.isCancel(e) || e.name === 'CanceledError') return;
        toast.error('Failed to load PO vs GRN analytics');
        setLoading(false);
      }
    }, 250);
    return () => { clearTimeout(id); ctrl.abort(); };
  }, [params]);

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const r = await axios.get(`${API}/reports/po-grn/export.${fmt}`, { headers: authHeaders(), params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `po_vs_grn_analytical_report_${new Date().toISOString().slice(0, 10)}.${fmt}`);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Report exported (${fmt.toUpperCase()})`);
      setLogRefresh((n) => n + 1);
    } catch (e) {
      toast.error('Export failed');
    }
    setDownloading(null);
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const suppliers = useMemo(() => data?.suppliers || [], [data]);
  const exceptions = useMemo(() => data?.exceptions || [], [data]);
  const grnRows = useMemo(() => rows.filter((r) => r.grn_number), [rows]);

  return (
    <div className="space-y-5" data-testid="po-grn-analytics-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')} data-testid="po-grn-back-btn"><ArrowLeft size={18} /></Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Scale className="text-indigo-600" size={28} /> PO vs GRN Analytical Dashboard
            </h1>
            <p className="text-sm text-slate-600 mt-1">Reconcile purchase orders against goods received — quantity, price, MRP, margin and supplier performance. All values incl. GST.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => download('xlsx')} disabled={!!downloading} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="po-grn-download-xlsx">
            {downloading === 'xlsx' ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Excel
          </Button>
          <Button onClick={() => download('csv')} disabled={!!downloading} variant="outline" className="gap-2" data-testid="po-grn-download-csv">
            {downloading === 'csv' ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} CSV
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-white"
          placeholder="Search PO number, GRN number, supplier, SKU, product, category, brand, invoice…" data-testid="po-grn-search-input" />
      </div>

      <GlobalFilters filters={filters} setFilters={setFilters} options={options} onReset={() => { setFilters({}); setSearch(''); }} />

      {history.length > 0 && (
        <div className="sticky top-0 z-30 -mx-1 px-1 py-2 bg-slate-50/90 backdrop-blur-md" data-testid="po-grn-back-bar">
          <div className="flex items-center gap-3 rounded-lg border border-indigo-200 bg-white px-3 py-2 shadow-sm">
            <Button size="sm" onClick={goBack} className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="po-grn-back-to-previous-btn">
              <Undo2 size={14} /> Back to previous view
            </Button>
            <span className="text-xs text-slate-500 truncate" data-testid="po-grn-back-label">
              Last action: {history[history.length - 1].label} · {history.length} step{history.length > 1 ? 's' : ''} back available
            </span>
            <button type="button" className="ml-auto text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline"
              onClick={() => setHistory([])} data-testid="po-grn-back-dismiss">Dismiss</button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={changeTab} className="relative">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1" data-testid="po-grn-tabs">
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} className="text-xs sm:text-sm" data-testid={`tab-${k}`}>{label}</TabsTrigger>
          ))}
          {loading && <Loader2 size={16} className="animate-spin text-indigo-600 ml-2 self-center" data-testid="po-grn-loading" />}
        </TabsList>
        {loading && (
          <div className="absolute inset-x-0 top-12 bottom-0 z-20 bg-white/60 backdrop-blur-[1px] flex items-start justify-center pt-24" data-testid="po-grn-loading-overlay">
            <div className="flex items-center gap-2 rounded-full bg-slate-900 text-white text-xs px-4 py-2 shadow-lg">
              <Loader2 size={14} className="animate-spin" /> Updating dashboard…
            </div>
          </div>
        )}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <MrpMismatchBanner items={data?.mrp_mismatches}
            onView={() => { pushView('Viewed MRP alerts'); document.getElementById('mrp-mismatch-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
            onFilterAll={() => { pushView('Filtered to MRP mismatches'); setFilters((f) => ({ ...f, exception_types: ['MRP Change'] })); }} />
          <KpiGrid kpis={data?.kpis} />
          <PeriodComparison comparison={data?.comparison} labels={cmp} />
          <OverviewCharts breakdowns={data?.breakdowns} suppliers={suppliers} onDrill={drill} />
          <MrpMismatchPanel items={data?.mrp_mismatches} onPick={pickMismatch} />
        </TabsContent>
        <TabsContent value="reconciliation" className="mt-4">
          <AnalyticsTable columns={RECON_COLS} rows={rows} testId="table-reconciliation" />
        </TabsContent>
        <TabsContent value="price" className="mt-4">
          <AnalyticsTable columns={PPV_COLS} rows={grnRows} testId="table-price-variance" />
        </TabsContent>
        <TabsContent value="mrp" className="mt-4 space-y-4">
          <MarginBreakdown byCategory={data?.margin_by_category} byBrand={data?.margin_by_brand} onPick={drill} />
          <AnalyticsTable columns={MRP_COLS} rows={grnRows} testId="table-mrp-margin" />
        </TabsContent>
        <TabsContent value="supplier" className="mt-4">
          <AnalyticsTable columns={SUPPLIER_COLS} rows={suppliers} testId="table-supplier-performance" />
        </TabsContent>
        <TabsContent value="exceptions" className="mt-4">
          <AnalyticsTable columns={EXCEPTION_COLS} rows={exceptions} testId="table-exceptions" rowKey={(r) => r.exception_id} />
        </TabsContent>
      </Tabs>

      <ReportDownloadLog reportKey="po-grn-analytics" refreshToken={logRefresh} />
    </div>
  );
}
